import { Direction, LanguageNameSpaces } from "@shared/types";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation";
import {
  formatFilledPercent,
  getLadderCounts,
  isGridOutOfRange,
  PriceLadderModel,
} from "common/NewLook/priceLadder.ts";

const percent = (value: number) => `${Math.round(value * 100)}%`;

/**
 * The ladder's one-line caption: what is filled ("2/3 entries · 1/4 TP",
 * "Waiting for the first entry", "TP 4/4" once closed), a grid's cycles or
 * range, or a hedging bot's long/short split.
 */
export const useLadderCaption = (
  model: PriceLadderModel,
  formatPrice: (price: number) => string
): { text: string; isWarning?: boolean } => {
  const { t: tBots } = useTypedTranslation(LanguageNameSpaces.BOTS);
  const { t: tSignals } = useTypedTranslation(LanguageNameSpaces.SIGNALS);
  const { t: tOnboarding, i18n } = useTypedTranslation(
    LanguageNameSpaces.ONBOARDING
  );
  if (model.type === "split") {
    if (model.longShare === undefined) {
      return {
        text:
          model.side === Direction.SHORT
            ? tBots("labels.short")
            : tBots("labels.long"),
      };
    }
    return {
      text: `${tBots("labels.long")} ${percent(model.longShare)} · ${tBots(
        "labels.short"
      )} ${percent(1 - model.longShare)}`,
    };
  }
  if (model.type === "grid") {
    const outOfRange = isGridOutOfRange(model);
    if (model.cycles !== undefined && !outOfRange) {
      return {
        // Proposed key (trades.json): "progress.grid_cycles".
        text: i18n.t("progress.grid_cycles", {
          ns: LanguageNameSpaces.TRADES,
          count: model.cycles,
          defaultValue: "{{count}} cycles",
          defaultValue_one: "{{count}} cycle",
        }) as string,
      };
    }
    return {
      text: `${formatPrice(model.min)} – ${formatPrice(model.max)}`,
      isWarning: outOfRange,
    };
  }
  const entriesWord = tSignals("edit_signal.target_modal.entries");
  const waiting = tOnboarding("revision.dca_visual0");
  const isClosed = model.isClosed ?? model.closePrice !== undefined;
  if (model.isComplete) {
    const { entries, takeProfits, stops } = getLadderCounts(model);
    if (isClosed) {
      if (takeProfits.total) {
        return {
          text: `TP ${takeProfits.filled}/${takeProfits.total}${
            stops.filled ? " · SL" : ""
          }`,
        };
      }
      return {
        text:
          model.closeStatus ||
          `${entries.filled}/${entries.total} ${entriesWord}`,
      };
    }
    if (!entries.filled) return { text: waiting };
    return {
      text: `${entries.filled}/${entries.total} ${entriesWord}${
        takeProfits.total
          ? ` · ${takeProfits.filled}/${takeProfits.total} TP`
          : ""
      }`,
    };
  }
  // List rows without every order: the filled shares.
  const entry = model.progress?.entry ?? 0;
  const takeProfit = model.progress?.takeProfit ?? 0;
  if (isClosed && model.closeStatus) return { text: model.closeStatus };
  if (!entry && !isClosed) return { text: waiting };
  return {
    text: `${formatFilledPercent(entry)} ${entriesWord}${
      takeProfit ? ` · ${formatFilledPercent(takeProfit)} TP` : ""
    }`,
  };
};
